/**
 * Types for the snap system.
 */

import type { Point3D } from "../interaction/types";

/**
 * Polar angle mode presets.
 * - '90': Snap to 0°, 90°, 180°, 270°
 * - '45': Snap to 0°, 45°, 90°, 135°, 180°, 225°, 270°, 315°
 * - '30': Snap to 0°, 30°, 60°, 90°, ... (every 30°)
 */
export type PolarAngleMode = "90" | "45" | "30";

/**
 * Object snap mode names.
 * Maps to ODA OsnapMode enum values.
 */
export type ObjectSnapMode =
  | "endpoint"
  | "midpoint"
  | "center"
  | "node"
  | "quadrant"
  | "intersection"
  | "insertion"
  | "perpendicular"
  | "tangent"
  | "nearest"
  | "apparentIntersection"
  | "parallel";

/**
 * Map from ObjectSnapMode to ODA OsnapMode enum values.
 * kOsModeStart (13) is excluded - it's a sentinel value, not a real entity snap point.
 */
export const OSNAP_MODE_MAP: Record<ObjectSnapMode, number> = {
  endpoint: 1, // kOsModeEnd
  midpoint: 2, // kOsModeMid
  center: 3, // kOsModeCen
  node: 4, // kOsModeNode
  quadrant: 5, // kOsModeQuad
  intersection: 6, // kOsModeIntersec
  insertion: 7, // kOsModeIns
  perpendicular: 8, // kOsModePerp
  tangent: 9, // kOsModeTan
  nearest: 10, // kOsModeNear
  apparentIntersection: 11, // kOsModeApint
  parallel: 12, // kOsModePar
};

/**
 * Display/priority order for object snap modes, matching ODA OsnapMode
 * declaration order.
 */
export const OBJECT_SNAP_MODE_ORDER: ObjectSnapMode[] = [
  "endpoint",
  "midpoint",
  "center",
  "node",
  "quadrant",
  "intersection",
  "insertion",
  "perpendicular",
  "tangent",
  "nearest",
  "apparentIntersection",
  "parallel",
];

/**
 * Modes turned on when the user enables object snap without having picked any
 * mode yet — the historical bundle, useful without being noisy.
 */
export const DEFAULT_OBJECT_SNAP_MODES: ObjectSnapMode[] = [
  "endpoint",
  "midpoint",
  "nearest",
];

/**
 * Modes shown in the mode picker UI (snap/ObjectSnapTool.vue).
 *
 * "tangent" is deliberately excluded: confirmed not to work (same WASM
 * engine gap as perpendicular - getOsnapPoints likely returns 0 points for
 * kOsModeTan - but unlike perpendicular, no custom polyline fallback has
 * been written for it yet). It stays in ObjectSnapMode/OSNAP_MODE_MAP/
 * OBJECT_SNAP_MODE_ORDER (still toggleable programmatically via
 * DraggerMenuState.toggleObjectSnapMode) so it's ready to re-enable in the
 * UI once fixed.
 */
const EXCLUDED_SNAP_MODE: ObjectSnapMode[] = [
  "tangent",
  "apparentIntersection",
  "parallel",
];
export const OBJECT_SNAP_MODE_UI_ORDER: ObjectSnapMode[] =
  OBJECT_SNAP_MODE_ORDER.filter((mode) => !EXCLUDED_SNAP_MODE.includes(mode));

/**
 * Snap configuration.
 */
export interface SnapConfig {
  orthoEnabled: boolean;
  polarEnabled: boolean;
  polarAngleMode: PolarAngleMode;
  objectSnapEnabled: boolean;
  objectSnapModes: Set<ObjectSnapMode>;
  objectSnapAperture: number; // pixels
}

/**
 * Snap behavior options for draggers and handles.
 */
export interface SnapOptions {
  acceptOrtho: boolean;
  acceptPolar: boolean;
  acceptObject: boolean;
}

/**
 * Result of snap processing.
 */
export interface SnapResult {
  /** The snapped/constrained point */
  point: Point3D;
  /** Whether the point was constrained */
  constrained: boolean;
  /** Type of constraint applied */
  constraintType: "ortho" | "polar" | "osnap" | null;
  /** Object snap mode if osnap was applied */
  osnapMode?: ObjectSnapMode;
  /** Polar snap angle in degrees (0-360), set when constraintType === 'polar' */
  polarAngle?: number;
  /** Ortho axis, set when constraintType === 'ortho' */
  orthoAxis?: "horizontal" | "vertical";
}
